import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, ServiceConfig, TierItem } from '@common-fe/common-fe';

import HorizontalLine from '@/components/elements/HorizontalLine/HorizontalLine';
import { TriggerCategory } from '@/modules/deductibles/deductibles.types';
import { FEATURE_FLAGS, useFeatureFlag } from '@/utils/featureFlags';

import { LockCardPurseCard } from './LockCardPurseCard';
import { PayoutRulesCard } from './PayoutRulesCard';
import ServiceConfigItem from './ServiceConfigItem';
import { TriggerAmount } from './TriggerAmount';
import { TriggerNotification } from './TriggerNotification';

interface TierBlockProps {
  tier: TierItem;
  nextTier?: TierItem;
  numberOfTier?: string;
  isLastItem?: boolean;
  serviceConfigs?: ServiceConfig[];
  triggerAmounts?: Record<string, number>;
  isEmbeddedOnly?: boolean;
}

export const TierBlock: React.FC<TierBlockProps> = ({
  tier,
  nextTier,
  numberOfTier,
  isLastItem,
  serviceConfigs,
  triggerAmounts,
  isEmbeddedOnly,
}) => {
  const { t } = useTranslation();
  const isCardPurseLockEnabled = useFeatureFlag(FEATURE_FLAGS.CARD_PURSE_LOCK_BY_HEALTH_PLAN);

  const { payoutRuleType, planIdsForCardPurseLock, planNamesForCardPurseLock } = tier;

  const getTriggerName = (triggerCategoryType?: string) => {
    if (triggerCategoryType === TriggerCategory.FAMILY) {
      return t('Family');
    }
    if (triggerCategoryType === TriggerCategory.EMBEDDED) {
      return t('Individual');
    }
    return '';
  };

  return (
    <Box data-testid={`tier-block_${numberOfTier}`}>
      <Box
        pad={{ vertical: 'm', horizontal: 'l' }}
        round="container1Round"
        background="canvas"
        border={{ color: 'border2', size: '1px' }}
      >
        <Inscription
          color="textBody"
          size="xxlarge"
          weight={400}
          lineHeight="28px"
          margin={{ bottom: isCardPurseLockEnabled || tier?.tierTriggers?.length ? 'm' : '0' }}
        >
          {tier?.name || ''}
        </Inscription>
        {isCardPurseLockEnabled && (
          <>
            <Box direction="row" gap="l">
              <PayoutRulesCard payoutRuleType={payoutRuleType} />
              {!isLastItem && !isEmbeddedOnly && (
                <LockCardPurseCard
                  planIdsForCardPurseLock={planIdsForCardPurseLock}
                  planNamesForCardPurseLock={planNamesForCardPurseLock}
                />
              )}
            </Box>
            {tier?.tierTriggers?.length ? <HorizontalLine margin={{ vertical: 'spacing16' }} /> : null}
          </>
        )}
        {tier?.tierTriggers?.length ? (
          <Box direction="row" gap="l">
            <Box width="35%">
              <Inscription color="textBody" size="large" lineHeight="22px" margin={{ bottom: 's' }}>
                {t('Deductible met notification')}
              </Inscription>
              <Box background="module" pad="s" round="container1Round">
                <TriggerNotification isSendTriggerNotification={tier?.isSendTriggerNotification} />
              </Box>
            </Box>
            <Box width="65%">
              <Inscription color="textBody" lineHeight="22px" size="large" margin={{ bottom: 's' }}>
                {tier?.name || ''} to {nextTier?.name || ''}
              </Inscription>
              <Box background="module" pad="s" round="container1Round" gap="xs">
                {tier?.tierTriggers?.map((trigger) => (
                  <TriggerAmount
                    key={trigger.id}
                    triggerName={getTriggerName(trigger.triggerCategoryType)}
                    triggerAmount={triggerAmounts?.[trigger.id ?? ''] || 0}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
      {serviceConfigs
        ?.filter((service) => service?.processingTierId === tier?.id)
        ?.map((item, indexEl) => (
          <ServiceConfigItem key={item?.id} value={item} isFirstItem={indexEl === 0} />
        ))}
      {!isLastItem && <Box height="1px" margin={{ vertical: 'm' }} background={{ color: 'border1' }} />}
    </Box>
  );
};
